import { StrictMode, lazy, Suspense, useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { auth } from './firebase/init';
import { signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { verifyAuthInitialization, getAuthErrorMessage } from './trustgate/guards/authInitializationGuard';
import { AuthInitializationErrorPanel } from './trustgate/guards/AuthInitializationErrorPanel';
import './index.css';

// Service worker kill-switch: a stale SW from a prior deploy intercepts module
// fetches and returns cached index.html (text/html) for lazy chunks, causing
// MIME type errors. Force SW update check and cleanup on load.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistration().then((registration) => {
    if (registration) {
      // Force update check immediately
      registration.update();
      // Unregister after a short delay to allow update to complete
      setTimeout(() => {
        registration.unregister();
        if ('caches' in window) {
          caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key))));
        }
      }, 1000);
    }
  }).catch(() => {
    /* fail open: no SW to clean up */
  });
}

// Global error handler for Firestore SDK internal assertion errors
// This is a workaround for Firebase Firestore SDK 12.0.0 listener cleanup bugs
// 
// GUARDRAIL: Only suppress the known internal assertion signature
// - Pattern: "FIRESTORE" + "INTERNAL ASSERTION"
// - This specifically targets SDK-level watch stream state bugs during unmount
// - Real Firestore permission/network errors (permission-denied, unavailable, etc.) are NOT suppressed
// - Other Firebase errors are NOT suppressed
window.addEventListener('error', (event) => {
  if (event.message?.includes('FIRESTORE') && event.message?.includes('INTERNAL ASSERTION')) {
    console.warn('[Firestore] Suppressed SDK assertion error:', event.error);
    event.preventDefault();
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.message?.includes('FIRESTORE') && event.reason?.message?.includes('INTERNAL ASSERTION')) {
    console.warn('[Firestore] Suppressed SDK assertion rejection:', event.reason);
    event.preventDefault();
  }
});

// Lazy load TrustGate views
// ROUTING ARCHITECTURE NOTES:
// - This file defines the TOP-LEVEL routing for the entire application
// - Console routes (/console/*) are delegated to TrustGateRouter (see src/trustgate/console/TrustGateRouter.tsx)
// - TrustGateRouter uses RELATIVE paths since it's nested under /console/*
// - Do NOT add BrowserRouter in TrustGateRouter - it's already provided here
// - For console route changes, edit TrustGateRouter.tsx, not this file
// - For top-level route changes (verify, governance, marketplace), edit this file
const TrustGateConsole = lazy(() => import('./trustgate/console/TrustGateConsole').then(m => ({ default: m.TrustGateConsole })));
const TrustGateLanding = lazy(() => import('./trustgate/console/TrustGateLanding').then(m => ({ default: m.TrustGateLanding })));
// Import TrustGateRouter directly to avoid dynamic import issues
import { TrustGateRouter } from './trustgate/console/TrustGateRouter';
const MarketplaceHome = lazy(() => import('./trustgate/marketplace/MarketplaceHome').then(m => ({ default: m.MarketplaceHome })));
const EpochContainer = lazy(() => import('./trustgate/epoch/EpochContainer').then(m => ({ default: m.EpochContainer })));
const PolicyPacksContainer = lazy(() => import('./trustgate/policy_packs/PolicyPacksContainer').then(m => ({ default: m.PolicyPacksContainer })));
const RequestsInbox = lazy(() => import('./trustgate/console/RequestsInbox').then(m => ({ default: m.RequestsInbox })));
const RequestDetail = lazy(() => import('./trustgate/console/RequestDetail').then(m => ({ default: m.RequestDetail })));
const ReceiptVerifierPage = lazy(() => import('./trustgate/console/ReceiptVerifierPage').then(m => ({ default: m.ReceiptVerifierPage })));
const OpsEvents = lazy(() => import('./trustgate/console/OpsEvents').then(m => ({ default: m.OpsEvents })));
const PublicProofViewer = lazy(() => import('./trustgate/public/PublicProofViewer').then(m => ({ default: m.PublicProofViewer })));
const VerifyHome = lazy(() => import('./trustgate/public/VerifyHome').then(m => ({ default: m.VerifyHome })));
const VerifyByHash = lazy(() => import('./trustgate/public/VerifyByHash').then(m => ({ default: m.VerifyByHash })));
const VerifyBundle = lazy(() => import('./trustgate/public/VerifyBundle').then(m => ({ default: m.VerifyBundle })));
const PublicBundleVerifier = lazy(() => import('./trustgate/public/PublicBundleVerifier').then(m => ({ default: m.PublicBundleVerifier })));
const CivilDisclosureShareViewer = lazy(() => import('./trustgate/public/CivilDisclosureShareViewer').then(m => ({ default: m.CivilDisclosureShareViewer })));
const GovernanceViewer = lazy(() => import('./trustgate/public/GovernanceViewer').then(m => ({ default: m.GovernanceViewer })));
const CivilDisclosureWorkspace = lazy(() => import('./trustgate/console/CivilDisclosureWorkspace').then(m => ({ default: m.CivilDisclosureWorkspace })));
const SessionConsole = lazy(() => import('./trustgate/console/SessionConsole').then(m => ({ default: m.SessionConsole })));
const GovernanceWorkspaceRoute = lazy(() => import('./trustgate/governanceWorkspace/GovernanceWorkspaceRoute').then(m => ({ default: m.GovernanceWorkspaceRoute })));
const AuthModal = lazy(() => import('./components/Auth/AuthModal').then(m => ({ default: m.AuthModal })));
const InviteOnlyScreen = lazy(() => import('./components/Auth/InviteOnlyScreen').then(m => ({ default: m.InviteOnlyScreen })));

// Safe shell routes for workspace surface boundary (client/ only - browser-safe)
const FormationGate = lazy(() => import('./trustgate/workspace/client/FormationGate').then(m => ({ default: m.FormationGate })));
const ConsolePreview = lazy(() => import('./trustgate/workspace/client/ConsolePreview').then(m => ({ default: m.ConsolePreview })));
const VerifyReceipt = lazy(() => import('./trustgate/workspace/client/VerifyReceipt').then(m => ({ default: m.VerifyReceipt })));
const PrivateWorkspaceShell = lazy(() => import('./trustgate/workspace/client/PrivateWorkspaceShell').then(m => ({ default: m.PrivateWorkspaceShell })));
// Formation-gate components
const GWUDemo = lazy(() => import('./trustgate/formation-gate/GWUDemo').then(m => ({ default: m.GWUDemo })));
const GWURegistry = lazy(() => import('./trustgate/formation-gate/GWURegistry').then(m => ({ default: m.GWURegistry })));
const GWUAppRegistry = lazy(() => import('./trustgate/formation-gate/GWUAppRegistry').then(m => ({ default: m.GWUAppRegistry })));
const CompatibilityReceiptIndex = lazy(() => import('./trustgate/formation-gate/CompatibilityReceiptIndex').then(m => ({ default: m.CompatibilityReceiptIndex })));

// Loading fallback
const LazyLoadingFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#16213e] flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00d4aa] mx-auto mb-4" />
      <p className="text-[#cbd5e1]">Loading TrustGate...</p>
    </div>
  </div>
);

// Auth wrapper for TrustGate Console

const TrustGateConsoleAuth = () => {
  const { user, loading, isAdmin, isInvited, invitationLoading, invitationStatus } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleSignOut = async () => {
    try {
      await firebaseSignOut(auth);
      window.location.href = '/';
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  // Title for TrustGate domain
  useEffect(() => {
    document.title = 'TrustGate - Governance & Verification Console';
  }, []);

  // Loading initial auth state
  if (loading) return <LazyLoadingFallback />;

  // Not authenticated - show landing with sign-in modal
  if (!user) {
    return (
      <>
        <Suspense fallback={<LazyLoadingFallback />}>
          <TrustGateLanding onSignInClick={() => setShowAuthModal(true)} />
        </Suspense>
        
        <Suspense fallback={null}>
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            defaultMode="signin"
            onSuccess={() => setShowAuthModal(false)}
          />
        </Suspense>
      </>
    );
  }

  // Authenticated - check invitation status
  if (invitationLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#16213e] flex items-center justify-center text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00d4aa] mx-auto mb-4"></div>
          <p className="text-[#cbd5e1]">Verifying access...</p>
        </div>
      </div>
    );
  }

  // User is invited/admin - show TrustGate Console
  if (isInvited) {
    return <TrustGateConsole user={user} isAdmin={isAdmin} onSignOut={handleSignOut} />;
  }

  // User is NOT invited - show invite-only screen
  return (
    <Suspense fallback={<LazyLoadingFallback />}>
      <InviteOnlyScreen 
        email={user.email} 
        reason={invitationStatus?.reason}
      />
    </Suspense>
  );
};

// Auth gate for all routes - requires authentication, redirects back to intended route after sign-in
const TrustGateDomainAuthGate = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();
  const navigate = useNavigate();
  const [firebaseUser, setFirebaseUser] = useState(auth.currentUser);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const didStorePathRef = useRef(false);

  console.log('[RouteTrace] TrustGateDomainAuthGate rendered', window.location.pathname);

  // Store intended path once on mount
  useEffect(() => {
    if (!didStorePathRef.current) {
      const currentPath = window.location.pathname;
      if (currentPath !== '/' && currentPath !== '/verify' && currentPath !== '/governance') {
        console.log('[AuthGate] Storing intended path:', currentPath);
        sessionStorage.setItem('authRedirectPath', currentPath);
      }
      didStorePathRef.current = true;
    }
  }, []);

  // Subscribe directly to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      console.log('[AuthGate] Firebase user updated:', fbUser?.email ?? 'null');
      setFirebaseUser(fbUser);
      if (fbUser) {
        const storedPath = sessionStorage.getItem('authRedirectPath');
        console.log('[AuthGate] storedPath =', storedPath);
        if (storedPath && storedPath !== '/') {
          sessionStorage.removeItem('authRedirectPath');
          console.log('[AuthGate] Navigating to:', storedPath);
          navigate(storedPath, { replace: true });
        }
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  if (loading) return <LazyLoadingFallback />;

  if (!firebaseUser) {
    return (
      <>
        <Suspense fallback={<LazyLoadingFallback />}>
          <TrustGateLanding onSignInClick={() => setShowAuthModal(true)} />
        </Suspense>
        <Suspense fallback={null}>
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            defaultMode="signin"
            onSuccess={() => setShowAuthModal(false)}
          />
        </Suspense>
      </>
    );
  }

  return <>{children}</>;
};

// Wrapper to pass auth context to TrustGateRouter (assumes TrustGateDomainAuthGate handled auth)
const TrustGateRouterAuthWrapper = () => {
  const { user, isAdmin } = useAuth();

  if (!user) {
    console.log('[TrustGateRouterAuthWrapper] Waiting for user context...');
    return <LazyLoadingFallback />;
  }

  return <TrustGateRouter user={user} isAdmin={isAdmin} />;
};

// Root component with all providers
const Root = () => {
  // Fail-closed: Verify Firebase Auth initialization before rendering
  const authInitResult = verifyAuthInitialization();

  if (!authInitResult.success) {
    // Log to console for developer visibility
    console.error('[TrustGate] Auth initialization failed:', authInitResult);
    
    // Show visible error panel for local dev
    return (
      <StrictMode>
        <AuthInitializationErrorPanel errorMessage={getAuthErrorMessage(authInitResult)} />
      </StrictMode>
    );
  }

  return (
    <StrictMode>
      <Router>
        <AuthProvider>
          <Routes>
          {/* Public routes - gated on trustgate.dev */}
          <Route 
            path="/verify/:id" 
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <PublicBundleVerifier />
                </Suspense>
              </TrustGateDomainAuthGate>
            } 
          />
          {/* Legacy receipt verification route */}
          <Route 
            path="/verify/:proofId" 
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <PublicProofViewer />
                </Suspense>
              </TrustGateDomainAuthGate>
            } 
          />
          <Route 
            path="/verify" 
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <VerifyHome />
                </Suspense>
              </TrustGateDomainAuthGate>
            } 
          />
          <Route 
            path="/verify-hash" 
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <VerifyByHash />
                </Suspense>
              </TrustGateDomainAuthGate>
            } 
          />
          <Route 
            path="/verify-bundle" 
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <VerifyBundle />
                </Suspense>
              </TrustGateDomainAuthGate>
            } 
          />
          {/* Compatibility alias for /verify/:id - same component, same behavior */}
          <Route 
            path="/verify-bundle/:id" 
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <PublicBundleVerifier />
                </Suspense>
              </TrustGateDomainAuthGate>
            } 
          />
          <Route 
            path="/civil-disclosure-share/:sliceId" 
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <CivilDisclosureShareViewer />
                </Suspense>
              </TrustGateDomainAuthGate>
            } 
          />
          <Route 
            path="/governance/:bundleId" 
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <GovernanceViewer />
                </Suspense>
              </TrustGateDomainAuthGate>
            } 
          />
          <Route 
            path="/governance" 
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <GovernanceViewer />
                </Suspense>
              </TrustGateDomainAuthGate>
            } 
          />
          
          {/* Protected routes */}
          <Route
            path="/marketplace"
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <MarketplaceHome onSelectModule={(id) => console.log('Selected module:', id)} />
                </Suspense>
              </TrustGateDomainAuthGate>
            }
          />
          <Route
            path="/policy-packs"
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <PolicyPacksContainer />
                </Suspense>
              </TrustGateDomainAuthGate>
            }
          />
          <Route
            path="/policy-packs/:packId"
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <PolicyPacksContainer />
                </Suspense>
              </TrustGateDomainAuthGate>
            }
          />
          <Route
            path="/requests"
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <RequestsInbox />
                </Suspense>
              </TrustGateDomainAuthGate>
            }
          />
          <Route
            path="/requests/:requestId"
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <RequestDetail />
                </Suspense>
              </TrustGateDomainAuthGate>
            }
          />
          <Route
            path="/receipt-verifier"
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <ReceiptVerifierPage />
                </Suspense>
              </TrustGateDomainAuthGate>
            }
          />
          <Route
            path="/ops"
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <OpsEvents />
                </Suspense>
              </TrustGateDomainAuthGate>
            }
          />
          <Route
            path="/epoch"
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <EpochContainer />
                </Suspense>
              </TrustGateDomainAuthGate>
            }
          />
          <Route
            path="/civil-disclosure"
            element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <CivilDisclosureWorkspace user={{ email: 'user@example.com', uid: 'temp' }} onNavigateBack={() => window.history.back()} />
              </Suspense>
            }
          />
          <Route
            path="/session"
            element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <SessionConsole />
              </Suspense>
            }
          />
          {/* Console routes - handled by TrustGateRouter */}
          <Route
            path="/console/*"
            element={
              <TrustGateDomainAuthGate>
                <TrustGateRouterAuthWrapper />
              </TrustGateDomainAuthGate>
            }
          />
          <Route
            path="/governance-workspace"
            element={
              <TrustGateDomainAuthGate>
                <Suspense fallback={<LazyLoadingFallback />}>
                  <GovernanceWorkspaceRoute forceFixture={true} />
                </Suspense>
              </TrustGateDomainAuthGate>
            }
          />
          {/* Safe shell routes for workspace surface boundary */}
          <Route
            path="/formation-gate"
            element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <FormationGate />
              </Suspense>
            }
          />
          {/* Formation-gate routes */}
          <Route
            path="/formation-gate/gwu-demo"
            element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <GWUDemo />
              </Suspense>
            }
          />
          <Route
            path="/formation-gate/gwu-registry"
            element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <GWURegistry />
              </Suspense>
            }
          />
          <Route
            path="/formation-gate/gwu-apps"
            element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <GWUAppRegistry />
              </Suspense>
            }
          />
          <Route
            path="/formation-gate/compatibility-receipt-index"
            element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <CompatibilityReceiptIndex />
              </Suspense>
            }
          />
          <Route
            path="/formation-gate/console-preview"
            element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <ConsolePreview />
              </Suspense>
            }
          />
          <Route
            path="/formation-gate/verify"
            element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <VerifyReceipt />
              </Suspense>
            }
          />
          <Route
            path="/formation-gate/workspace"
            element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <PrivateWorkspaceShell />
              </Suspense>
            }
          />
                    {/* Console home - default route */}
          <Route 
            path="/" 
            element={
              <Suspense fallback={<LazyLoadingFallback />}>
                <TrustGateConsoleAuth />
              </Suspense>
            } 
          />
          
          {/* Catch-all redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  </StrictMode>
  );
};

// Render the app
const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');
createRoot(rootElement).render(<Root />);
